import { useState, type ReactNode } from 'react';

import { api, ErroApi } from '../api';
import { AGRUPA, FORMAS, formaSugerida, intencaoDaForma } from '../cartoes';
import { Aviso, Campo, useAcao } from '../comum';
import {
  converterDoServidor,
  ListaDeFiltros,
  paraDominio,
  type Filtro,
} from '../filtros';
import type { Cartao } from './Inicio';
import type { FormaCartao } from '../cartoes';

/**
 * Editor de cartão do painel.
 *
 * Dois caminhos para a mesma coisa, de propósito:
 *
 *   **Perguntando** — reescreve a pergunta em português e a IA remonta o
 *   filtro. É a promessa do produto: ninguém precisa aprender linguagem de
 *   relatório para montar um painel.
 *
 *   **Ajustando à mão** — o mesmo construtor de filtros da regra do Sentinela.
 *   Para quem sabe o que quer e precisa de precisão que a frase não dá.
 *
 * ⚠️ Os dois desembocam na **mesma estrutura**, compilada pelo mesmo código no
 * servidor. Não existe caminho privilegiado: o que a pergunta produz, a mão
 * pode corrigir, e vice-versa.
 */

interface Previa {
  readonly total: number;
  readonly descricao: string;
}

export function EditorCartao({
  cartao,
  irmaos,
  aoFechar,
  aoSalvar,
  aoPerderSessao,
}: {
  cartao: Cartao;
  /** Os outros cartões do painel — para avisar sobre duplicata. */
  irmaos?: readonly Cartao[];
  aoFechar: () => void;
  aoSalvar: (novo: Cartao) => void;
  aoPerderSessao: () => void;
}): ReactNode {
  const acao = useAcao(aoPerderSessao);
  const [erroIa, setErroIa] = useState<string | null>(null);
  const [avisoDeFormato, setAvisoDeFormato] = useState<string | null>(null);

  const [titulo, setTitulo] = useState(cartao.titulo);
  const [nota, setNota] = useState(cartao.nota ?? '');
  const [forma, setForma] = useState<FormaCartao>(cartao.forma);
  const [largura, setLargura] = useState(String(cartao.largura));
  const [limite, setLimite] = useState(
    cartao.limiteAlerta === null ? '' : String(cartao.limiteAlerta),
  );

  const [modo, setModo] = useState<'pergunta' | 'manual'>('pergunta');
  const [pergunta, setPergunta] = useState(cartao.consulta.explicacao ?? '');
  const [filtros, setFiltros] = useState<Filtro[]>(
    converterDoServidor(cartao.consulta.filtros),
  );
  const [agruparPor, setAgruparPor] = useState(cartao.consulta.agruparPor ?? '');
  /**
   * 🔴 A intenção **não é estado**: ela sai do formato.
   *
   * Eram dois campos independentes, e a pessoa montava "mostrar como número"
   * com uma consulta que agrupa — combinação que não existe. O servidor
   * recusava com 400 depois de todo o trabalho feito.
   */
  const intencao = intencaoDaForma(forma);
  const [explicacao, setExplicacao] = useState(cartao.consulta.explicacao ?? '');
  const [previa, setPrevia] = useState<Previa | null>(null);
  const [pensando, setPensando] = useState(false);

  const consultaAtual = (): Record<string, unknown> => ({
    intencao,
    filtros: filtros.map(paraDominio),
    agruparPor: agruparPor === '' ? null : agruparPor,
    ordenarPor: null,
    ordemDescendente: false,
    limite: 50,
    explicacao: explicacao.trim() === '' ? titulo : explicacao.trim(),
  });

  /**
   * Reescreve o filtro a partir da pergunta.
   *
   * O que volta é **mostrado antes de salvar** — "Entendi como" mais a
   * contagem. Sem isso a pessoa trocaria um filtro que funcionava por um que a
   * IA entendeu de outro jeito, e só descobriria dias depois olhando um número
   * errado no painel.
   */
  const reinterpretar = (): void => {
    if (pergunta.trim() === '') return;
    setPensando(true);
    void (async () => {
      setErroIa(null);
      try {
        const r = await api.post<{
          explicacao: string;
          consulta: {
            intencao: string;
            filtros: readonly unknown[];
            agruparPor: string | null;
          };
          resultado: { total?: number };
        }>('/api/ia/perguntar', { pergunta });

        setFiltros(converterDoServidor(r.consulta.filtros));
        setAgruparPor(r.consulta.agruparPor ?? '');
        /*
          A IA pode ter entendido uma pergunta de outro tipo — "quantas" virou
          "quais". O formato acompanha, em vez de virar erro na hora de salvar.
        */
        const sugerida = formaSugerida(r.consulta.intencao);
        if (sugerida !== forma) {
          setForma(sugerida);
          setAvisoDeFormato(
            `A pergunta mudou de tipo, então o cartão passou a "${sugerida}".`,
          );
        } else {
          setAvisoDeFormato(null);
        }
        setExplicacao(r.explicacao);
        setPrevia({ total: r.resultado.total ?? 0, descricao: r.explicacao });
      } catch (e: unknown) {
        if (e instanceof ErroApi && e.precisaLogin) {
          aoPerderSessao();
          return;
        }
        setPrevia(null);
        setErroIa(e instanceof Error ? e.message : String(e));
      } finally {
        setPensando(false);
      }
    })();
  };

  /** Conta quantas máquinas o filtro atual devolve, sem salvar nada. */
  const conferir = (): void => {
    void acao.rodar(async () => {
      const r = await api.post<{ total: number; descricao: string }>(
        '/api/dispositivos/consulta',
        { consulta: consultaAtual(), limite: 1 },
      );
      setPrevia({ total: r.total, descricao: r.descricao });
      return null;
    });
  };

  const salvar = (): void => {
    void acao.rodar(async () => {
      if (titulo.trim() === '') throw new Error('O cartão precisa de um título.');
      aoSalvar({
        ...cartao,
        titulo: titulo.trim().slice(0, 60),
        nota: nota.trim() === '' ? null : nota.trim(),
        forma,
        largura: Number(largura) as Cartao['largura'],
        limiteAlerta: limite.trim() === '' ? null : Number(limite),
        consulta: consultaAtual() as unknown as Cartao['consulta'],
      });
      return null;
    });
  };

  /**
   * Já existe um cartão medindo exatamente isto?
   *
   * Medido no painel real: "Windows 10 no parque" e "Maquina win 10" mostravam
   * o mesmo 10, lado a lado. Não é erro — às vezes a pessoa quer mesmo — mas
   * ninguém duplica de propósito sem perceber. Avisar é barato; bloquear seria
   * arrogante.
   */
  const duplicado = (irmaos ?? []).find(
    (o) =>
      o.id !== cartao.id &&
      JSON.stringify(o.consulta.filtros ?? []) === JSON.stringify(filtros.map(paraDominio)) &&
      (o.consulta.intencao ?? 'contar') === intencao,
  );

  return (
    <div className="editor-cartao">
      <header>
        <strong>Editar cartão</strong>
        <button className="icone" onClick={aoFechar} aria-label="Fechar">
          ✕
        </button>
      </header>

      {acao.erro !== null && <Aviso tipo="erro">{acao.erro}</Aviso>}
      {erroIa !== null && <Aviso tipo="erro">{erroIa}</Aviso>}
      {avisoDeFormato !== null && <Aviso tipo="alerta">{avisoDeFormato}</Aviso>}

      <div className="grade2">
        <Campo rotulo="Título">
          <input value={titulo} maxLength={60} onChange={(e) => setTitulo(e.target.value)} />
        </Campo>
        <Campo rotulo="Nota" ajuda="Frase curta sob o número. Ex.: fora de suporte.">
          <input value={nota} maxLength={80} onChange={(e) => setNota(e.target.value)} />
        </Campo>
        <Campo
          rotulo="Formato"
          ajuda="Define também o tipo da consulta — não dá para escolher uma combinação que o servidor recuse."
        >
          <select
            value={forma}
            onChange={(e) => {
              setForma(e.target.value as FormaCartao);
              setAvisoDeFormato(null);
            }}
          >
            {FORMAS.map((f) => (
              <option key={f.valor} value={f.valor}>
                {f.rotulo}
              </option>
            ))}
          </select>
        </Campo>
        <Campo rotulo="Largura">
          <select value={largura} onChange={(e) => setLargura(e.target.value)}>
            <option value="1">Estreito</option>
            <option value="2">Largo</option>
          </select>
        </Campo>
        <Campo
          rotulo="Acende em alerta acima de"
          ajuda="Vazio = nunca acende. É o que separa um painel bonito de um painel útil."
        >
          <input
            value={limite}
            inputMode="numeric"
            placeholder="(nunca)"
            onChange={(e) => setLimite(e.target.value.replace(/\D/g, ''))}
          />
        </Campo>
      </div>

      {/* ── Filtro ─────────────────────────────────────────────────────── */}
      <div className="abas-modo">
        <button
          className={modo === 'pergunta' ? 'ativo' : ''}
          onClick={() => setModo('pergunta')}
        >
          Perguntando
        </button>
        <button
          className={modo === 'manual' ? 'ativo' : ''}
          onClick={() => setModo('manual')}
        >
          Ajustando à mão
        </button>
      </div>

      {modo === 'pergunta' ? (
        <>
          <Campo
            rotulo="Pergunta"
            ajuda="Escreva como falaria. A IA monta o filtro; você confere antes de salvar."
          >
            <input
              value={pergunta}
              maxLength={300}
              placeholder="quantas máquinas ainda estão no Windows 10?"
              onChange={(e) => setPergunta(e.target.value)}
            />
          </Campo>
          <button onClick={reinterpretar} disabled={pensando || pergunta.trim() === ''}>
            {pensando ? 'Interpretando…' : 'Interpretar de novo'}
          </button>
        </>
      ) : (
        <>
          <ListaDeFiltros
            titulo="Condições"
            ajuda="Todas precisam valer ao mesmo tempo. É o mesmo construtor da regra do Sentinela."
            filtros={filtros}
            aoMudar={setFiltros}
          />
          {AGRUPA(forma) && (
            <Campo
              rotulo="Agrupado por"
              ajuda="Campo que divide as barras. Ex.: versao_windows, so_familia, hw_fabricante."
            >
              <input
                value={agruparPor}
                placeholder="versao_windows"
                onChange={(e) => setAgruparPor(e.target.value)}
              />
            </Campo>
          )}
          <button onClick={conferir} disabled={acao.executando}>
            {acao.executando ? 'Conferindo…' : 'Conferir quantas máquinas'}
          </button>
        </>
      )}

      {/*
        A conferência é obrigatória para a confiança, não para o fluxo.
        Trocar um filtro que funcionava por um que a IA entendeu de outro jeito,
        sem ver o resultado, é como o painel começa a mentir.
      */}
      {duplicado !== undefined && (
        <Aviso tipo="alerta">
          O cartão <strong>{duplicado.titulo}</strong> já mede exatamente isto. Dá para
          salvar assim mesmo — só não deixe dois números iguais no painel sem querer.
        </Aviso>
      )}

      {previa !== null && (
        <Aviso>
          <strong>Entendi como:</strong> {previa.descricao}
          <div style={{ marginTop: 4 }}>
            Resultado agora: <strong>{previa.total}</strong> dispositivo(s).
          </div>
        </Aviso>
      )}

      <div className="linha">
        <button className="primario encolhe" onClick={salvar} disabled={acao.executando}>
          Salvar cartão
        </button>
        <button className="encolhe" onClick={aoFechar}>
          Cancelar
        </button>
        <span className="cresce" />
      </div>
    </div>
  );
}
